import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Loader2, Send, Sparkles, X } from 'lucide-react';
import { sendChatToBot } from '../../../services/n8nService';
import { T05SpotlightBorder } from './T05SpotlightBorder';
import './t05-arko-widget.css';

const WELCOME =
  'Guten Tag! Ich bin ARKO – Ihr Ansprechpartner für Orbis, die All-in-One-Plattform von ARKORBIT AI Austria. Womit kann ich helfen – Module, ein Angebot oder eine Demo?';

const GREETING_RE =
  /^(hallo|grüß\s*gott|gruss\s*gott|guten\s+(tag|morgen|abend|nachmittag))[\s!.?,]*$/i;

function isSimpleGreeting(text: string): boolean {
  return GREETING_RE.test(text.trim());
}

const TEASER =
  'Darf ich Ihnen zeigen, wie viel Zeit Ihre Kanzlei monatlich verschwendet?';

const TEASER_DISMISS_KEY = 't05-arko-teaser-dismissed';

type ChatMessage = { role: 'user' | 'bot'; text: string };

interface T05ArkoContextValue {
  isOpen: boolean;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
}

const T05ArkoContext = createContext<T05ArkoContextValue | null>(null);

function useT05Arko(): T05ArkoContextValue {
  const ctx = useContext(T05ArkoContext);
  if (!ctx) {
    throw new Error('T05Arko components must be used within T05ArkoProvider');
  }
  return ctx;
}

export const T05ArkoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);
  const toggleChat = useCallback(() => setIsOpen((o) => !o), []);

  return (
    <T05ArkoContext.Provider value={{ isOpen, toggleChat, openChat, closeChat }}>
      {children}
    </T05ArkoContext.Provider>
  );
};

export const T05ArkoNavButton: React.FC = () => {
  const { isOpen, toggleChat } = useT05Arko();
  const [showTeaser, setShowTeaser] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(TEASER_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (teaserDismissed || isOpen) return;
    const timer = window.setTimeout(() => setShowTeaser(true), 3000);
    return () => window.clearTimeout(timer);
  }, [teaserDismissed, isOpen]);

  useEffect(() => {
    if (isOpen) setShowTeaser(false);
  }, [isOpen]);

  const dismissTeaser = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTeaser(false);
    setTeaserDismissed(true);
    try {
      sessionStorage.setItem(TEASER_DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="t05-nav__arko-slot">
      {showTeaser && !isOpen && (
        <div className="t05-arko-widget__teaser" role="status">
          <button
            type="button"
            className="t05-arko-widget__teaser-dismiss"
            onClick={dismissTeaser}
            aria-label="Hinweis schließen"
          >
            <X size={12} aria-hidden />
          </button>
          <p>
            <strong>ARKO:</strong> {TEASER}
          </p>
        </div>
      )}

      <button
        type="button"
        className={`t05-nav__arko-btn${isOpen ? ' t05-nav__arko-btn--active' : ''}`}
        onClick={toggleChat}
        aria-expanded={isOpen}
        aria-controls="t05-arko-panel"
        aria-label={isOpen ? 'ARKO Chat schließen' : 'ARKO Chat öffnen'}
      >
        <Sparkles size={15} aria-hidden className="t05-nav__arko-btn-icon" />
        <span className="t05-nav__arko-btn-label">ARKO</span>
        {!isOpen && <span className="t05-nav__arko-btn-pulse" aria-hidden />}
      </button>
    </div>
  );
};

export const T05ArkoDropPanel: React.FC = () => {
  const { isOpen, closeChat } = useT05Arko();
  const [messages, setMessages] = useState<ChatMessage[]>([{ role: 'bot', text: WELCOME }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 280);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeChat();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeChat]);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const panel = document.getElementById('t05-arko-panel');
      const navSlot = document.querySelector('.t05-nav__arko-slot');
      const target = e.target as Node;
      if (panel?.contains(target) || navSlot?.contains(target)) return;
      closeChat();
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDown);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isOpen, closeChat]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: userMessage }]);
    setIsLoading(true);

    try {
      const response = await sendChatToBot(userMessage, messages, {
        intent: isSimpleGreeting(userMessage) ? 'greeting' : undefined,
      });
      setMessages((prev) => [...prev, { role: 'bot', text: response }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: 'Verbindung zum Orbit-Knoten unterbrochen. Bitte versuchen Sie es später erneut.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      id="t05-arko-panel"
      className="t05-arko-widget__drop"
      role="dialog"
      aria-label="ARKO KI-Assistent"
      aria-modal="false"
    >
        <T05SpotlightBorder className="t05-arko-widget__spotlight" size={520} intensity={0.55}>
          <div className="t05-arko-widget__panel">
            <header className="t05-arko-widget__header">
              <div className="t05-arko-widget__avatar" aria-hidden>
                <img src="/arkorbit-wappen.png" alt="" width={40} height={40} />
              </div>
              <div className="t05-arko-widget__meta">
                <h2 className="t05-arko-widget__title">ARKO - KI-Assistent</h2>
                <p className="t05-arko-widget__node">
                  <span className="t05-arko-widget__node-dot" aria-hidden />
                  NODE: FRANKFURT-ACTIVE
                </p>
              </div>
              <button
                type="button"
                className="t05-arko-widget__header-close"
                onClick={closeChat}
                aria-label="Chat schließen"
              >
                <X size={16} aria-hidden />
              </button>
            </header>

            <div ref={scrollRef} className="t05-arko-widget__messages">
              {messages.map((msg, i) => (
                <div
                  key={`${msg.role}-${i}`}
                  className={`t05-arko-widget__bubble t05-arko-widget__bubble--${msg.role}`}
                >
                  {msg.text}
                </div>
              ))}
              {isLoading && (
                <div className="t05-arko-widget__typing" aria-label="ARKO schreibt">
                  <span />
                  <span />
                  <span />
                </div>
              )}
            </div>

            <form className="t05-arko-widget__composer" onSubmit={handleSend}>
              <div className="t05-arko-widget__input-row">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ihre Nachricht..."
                  aria-label="Nachricht an ARKO"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  className="t05-arko-widget__send"
                  disabled={isLoading || !input.trim()}
                  aria-label="Nachricht senden"
                >
                  {isLoading ? (
                    <Loader2 size={18} className="t05-arko-widget__spin" />
                  ) : (
                    <Send size={18} />
                  )}
                </button>
              </div>
              <p className="t05-arko-widget__secure">VERSCHLÜSSELT VIA RSA-4096 &amp; TLS 1.3</p>
            </form>
          </div>
        </T05SpotlightBorder>
    </div>
  );
};

/** @deprecated Use T05ArkoProvider + T05ArkoNavButton + T05ArkoDropPanel */
export const T05ArkoWidget: React.FC = () => (
  <>
    <T05ArkoNavButton />
    <T05ArkoDropPanel />
  </>
);
